import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../utils/api';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';

const MyPageContainer = styled.div`
  max-width: 800px;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 2rem;
`;

const ProfileCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const ProfileHeader = styled.div`
  text-align: center;
  padding-bottom: 2rem;
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 2rem;
`;

const ProfileIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 1rem;
`;

const ProfileName = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ProfileRole = styled.div`
  font-size: 1rem;
  color: #6c757d;
  font-weight: 600;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1.25rem 0;
  border-bottom: 1px solid #dee2e6;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const InfoLabel = styled.span`
  font-size: 0.9375rem;
  color: #6c757d;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const InfoValue = styled.span`
  font-size: 0.9375rem;
  color: #000000;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const StatusBadge = styled.span<{ status?: string }>`
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => {
    if (props.status === '정상') return '#d1e7dd';
    if (props.status === '휴면') return '#fff3cd';
    return '#f8f9fa';
  }};
  color: ${props => {
    if (props.status === '정상') return '#0f5132';
    if (props.status === '휴면') return '#856404';
    return '#495057';
  }};
`;

const RoleBadge = styled.span<{ role?: string }>`
  padding: 0.375rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  background: ${props => {
    if (props.role === '회장') return '#e7f5ff';
    if (props.role === '부회장') return '#fff3e0';
    if (props.role === '총무') return '#f3e5f5';
    if (props.role === '매니저') return '#e8f5e9';
    return '#f8f9fa';
  }};
  color: ${props => {
    if (props.role === '회장') return '#1971c2';
    if (props.role === '부회장') return '#e67700';
    if (props.role === '총무') return '#9c27b0';
    if (props.role === '매니저') return '#2e7d32';
    return '#495057';
  }};
`;

const AccountCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin-bottom: 1.5rem;
`;

const ActionButton = styled(Button)`
  margin-top: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.875rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #000000;
  }
  
  &::placeholder {
    color: #adb5bd;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

interface MemberInfo {
  name: string;
  phone: string;
  birthYear: string;
  address: string;
  gender: string;
  electionStatus: string;
  status: string;
  registrationDate: string;
  dormantStatus: string;
  username: string;
}

const MemberMyPage: React.FC = () => {
  const { user } = useAuth();
  const [memberInfo, setMemberInfo] = useState<MemberInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDormantModalOpen, setIsDormantModalOpen] = useState(false);
  const [dormantFormData, setDormantFormData] = useState({
    start_date: '',
    dor_reason: ''
  });
  const [hasPendingRequest, setHasPendingRequest] = useState(false);

  // 회원 상세 정보 조회
  const fetchMemberInfo = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/members/me');
      if (response.data.success) {
        const data = response.data.data;
        setMemberInfo({
          name: data.name,
          phone: data.phone,
          birthYear: data.birth_date,
          address: data.address,
          gender: data.gender,
          electionStatus: data.is_elected === 'Y' ? '선출' : '비선출',
          status: data.status === 'A' ? '활동' : data.status === 'R' ? '신청' : '휴면',
          registrationDate: data.registered_date,
          dormantStatus: data.status === 'D' ? 'y' : 'n',
          username: data.user_id
        });
      }
    } catch (error) {
      console.error('회원 정보 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemberInfo();
    checkPendingRequest();
  }, []);

  const checkPendingRequest = async () => {
    try {
      const response = await apiClient.get('/dormant-requests?status=대기');
      if (response.data.success && response.data.data.length > 0) {
        // 현재 사용자의 대기 중인 신청이 있는지 확인
        const userRequest = response.data.data.find((req: any) => req.user_no === user?.user_no);
        setHasPendingRequest(!!userRequest);
      }
    } catch (error) {
      console.error('휴면 신청 확인 실패:', error);
    }
  };

  const handleDormantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!dormantFormData.start_date) {
      window.alert('휴면 시작일을 입력해주세요.');
      return;
    }

    try {
      const response = await apiClient.post('/dormant-request', {
        start_date: dormantFormData.start_date,
        dor_reason: dormantFormData.dor_reason || null
      });

      if (response.data.success) {
        window.alert(response.data.message || '휴면 신청이 완료되었습니다. 관리자 승인을 기다려주세요.');
        setIsDormantModalOpen(false);
        setDormantFormData({ start_date: '', dor_reason: '' });
        setHasPendingRequest(true);
      }
    } catch (error: any) {
      console.error('휴면 신청 실패:', error);
      const errorMessage = error.response?.data?.detail || error.message || '휴면 신청에 실패했습니다.';
      window.alert(errorMessage);
      console.error('에러 상세:', error.response?.data);
    }
  };

  const handleDormantRelease = async () => {
    if (!window.confirm('휴면을 해제하시겠습니까?')) {
      return;
    }

    try {
      const response = await apiClient.post('/dormant-release');
      if (response.data.success) {
        window.alert('휴면이 해제되었습니다.');
        fetchMemberInfo();
      }
    } catch (error: any) {
      console.error('휴면 해제 실패:', error);
      const errorMessage = error.response?.data?.detail || error.message || '휴면 해제에 실패했습니다.';
      window.alert(errorMessage);
    }
  };

  const formatBirthYear = (birthYear: string): string => {
    if (birthYear.length >= 4) {
      return `${birthYear.slice(0, 2)}년생`;
    }
    return birthYear;
  };

  if (loading) {
    return (
      <DashboardLayout title="마이페이지">
        <MyPageContainer>
          <Title>마이페이지</Title>
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>로딩 중...</div>
        </MyPageContainer>
      </DashboardLayout>
    );
  }

  if (!memberInfo) {
    return (
      <DashboardLayout title="마이페이지">
        <MyPageContainer>
          <Title>마이페이지</Title>
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>회원 정보를 불러올 수 없습니다.</div>
        </MyPageContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="마이페이지">
      <MyPageContainer>
        <Title>마이페이지</Title>
        
        <ProfileCard>
          <ProfileHeader>
            <ProfileIcon>👤</ProfileIcon>
            <ProfileName>{memberInfo.name}</ProfileName>
            <ProfileRole>
              <RoleBadge role={memberInfo.status}>{memberInfo.status}</RoleBadge>
            </ProfileRole>
          </ProfileHeader>
          
          <SectionTitle>회원 정보</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>이름</InfoLabel>
              <InfoValue>{memberInfo.name}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>전화번호</InfoLabel>
              <InfoValue>{memberInfo.phone}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>생년월일</InfoLabel>
              <InfoValue>{formatBirthYear(memberInfo.birthYear)}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>주소</InfoLabel>
              <InfoValue>{memberInfo.address}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>성별</InfoLabel>
              <InfoValue>{memberInfo.gender}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>선출 여부</InfoLabel>
              <InfoValue>{memberInfo.electionStatus}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>회원 상태</InfoLabel>
              <InfoValue>
                <StatusBadge status={memberInfo.dormantStatus === 'n' ? '정상' : '휴면'}>
                  {memberInfo.dormantStatus === 'n' ? '정상' : '휴면'}
                </StatusBadge>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>가입일</InfoLabel>
              <InfoValue>{memberInfo.registrationDate}</InfoValue>
            </InfoRow>
          </InfoGrid>
        </ProfileCard>
        
        <AccountCard>
          <SectionTitle>계정 정보</SectionTitle>
          <InfoGrid>
            <InfoRow>
              <InfoLabel>아이디</InfoLabel>
              <InfoValue>{memberInfo.username}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>비밀번호</InfoLabel>
              <InfoValue>••••••••</InfoValue>
            </InfoRow>
          </InfoGrid>
          {memberInfo.dormantStatus === 'n' && (
            <ActionButton
              variant="outline"
              size="large"
              fullWidth
              onClick={() => setIsDormantModalOpen(true)}
              disabled={hasPendingRequest}
            >
              {hasPendingRequest ? '휴면 신청 대기 중' : '휴면 신청'}
            </ActionButton>
          )}
          {memberInfo.dormantStatus === 'y' && (
            <ActionButton
              variant="primary"
              size="large"
              fullWidth
              onClick={handleDormantRelease}
            >
              휴면 해제
            </ActionButton>
          )}
        </AccountCard>

        {/* 휴면 신청 모달 */}
        <ModalOverlay 
          isOpen={isDormantModalOpen}
          onClick={() => setIsDormantModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>휴면 신청</ModalTitle>
              <CloseButton onClick={() => setIsDormantModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            <ModalForm onSubmit={handleDormantSubmit}>
              <Input
                label="휴면 시작일"
                type="date"
                value={dormantFormData.start_date}
                onChange={(e) => setDormantFormData({ ...dormantFormData, start_date: e.target.value })}
                required
              />
              
              <div>
                <label style={{ fontSize: '0.875rem', fontWeight: 600, color: '#343a40', display: 'block', marginBottom: '0.625rem' }}>
                  휴면 사유
                </label>
                <TextArea
                  value={dormantFormData.dor_reason}
                  onChange={(e) => setDormantFormData({ ...dormantFormData, dor_reason: e.target.value })}
                  placeholder="휴면 사유를 입력하세요 (선택사항)"
                />
              </div>
              
              <ModalActions>
                <Button
                  type="button"
                  variant="outline"
                  size="large"
                  fullWidth
                  onClick={() => setIsDormantModalOpen(false)}
                >
                  취소
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                >
                  신청
                </Button>
              </ModalActions>
            </ModalForm>
          </ModalContent>
        </ModalOverlay>
      </MyPageContainer>
    </DashboardLayout>
  );
};

export default MemberMyPage;

